import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Mail } from 'lucide-react';
import { useContent } from '../../lib/content';
import { pickLocale } from '../../lib/api';

export default function BoldFooter() {
  const { content, locale } = useContent();
  const site = content.site || {};
  const footer = content.footer || {};
  const social = site.social || {};
  const tagline = pickLocale(footer.tagline, locale, 'Always shipping the future.');
  const businessEmail = footer.business_email;

  return (
    <footer
      data-testid="bold-footer"
      className="relative bg-bg pt-20 md:pt-28 pb-8 px-5 md:px-10 border-t border-line overflow-hidden"
    >
      <div className="container-x">
        {/* Massive tagline */}
        <motion.h2
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9, ease: [0.2, 0.8, 0.2, 1] }}
          className="text-block block-huge text-ink leading-[0.9] mb-12 md:mb-16 break-words hyphens-auto"
          data-testid="footer-tagline"
        >
          {tagline}
        </motion.h2>

        {/* Sub grid */}
        <div className="grid grid-cols-2 md:grid-cols-12 gap-8 md:gap-10 pb-12 border-t border-line pt-12">
          <div className="col-span-2 md:col-span-4">
            <p className="text-eyebrow mb-4">{locale === 'es' ? 'Páginas' : 'Pages'}</p>
            <ul className="space-y-2.5 font-mono text-sm">
              <li><a href="#manifesto" className="text-ink-muted hover:text-ink">{locale === 'es' ? 'Manifiesto' : 'Manifesto'}</a></li>
              <li><a href="#looks" className="text-ink-muted hover:text-ink">Looks</a></li>
              <li><a href="#drop" className="text-ink-muted hover:text-ink">{locale === 'es' ? 'Última colección' : 'Last drop'}</a></li>
              <li><a href="#partners" className="text-ink-muted hover:text-ink">Partners</a></li>
              <li><a href="#faq" className="text-ink-muted hover:text-ink">FAQ</a></li>
              <li><a href="#contact" className="text-ink-muted hover:text-ink">{locale === 'es' ? 'Contacto' : 'Contact'}</a></li>
              <li>
                <a href={site.app_login_url || '#'} target="_blank" rel="noreferrer" className="text-acid inline-flex items-center gap-1 hover:underline">
                  {locale === 'es' ? 'Acceder' : 'Sign in'} <ArrowUpRight size={11} />
                </a>
              </li>
            </ul>
          </div>

          <div className="col-span-2 md:col-span-4">
            <p className="text-eyebrow mb-4">{locale === 'es' ? 'Síguenos' : 'Follow'}</p>
            <ul className="space-y-2.5 font-mono text-sm">
              {social.instagram && <li><a href={social.instagram} target="_blank" rel="noreferrer" className="text-ink-muted hover:text-ink">Instagram</a></li>}
              {social.tiktok && <li><a href={social.tiktok} target="_blank" rel="noreferrer" className="text-ink-muted hover:text-ink">TikTok</a></li>}
              {social.linkedin && <li><a href={social.linkedin} target="_blank" rel="noreferrer" className="text-ink-muted hover:text-ink">LinkedIn</a></li>}
              {social.youtube && <li><a href={social.youtube} target="_blank" rel="noreferrer" className="text-ink-muted hover:text-ink">YouTube</a></li>}
              {social.x && <li><a href={social.x} target="_blank" rel="noreferrer" className="text-ink-muted hover:text-ink">X</a></li>}
            </ul>
          </div>

          <div className="col-span-2 md:col-span-4">
            <p className="text-eyebrow mb-4">{locale === 'es' ? 'Negocios' : 'Business'}</p>
            <ul className="space-y-2.5 font-mono text-sm">
              <li><Link to="/admin" className="text-ink-muted hover:text-ink">Admin</Link></li>
              {businessEmail && (
                <li>
                  <a href={`mailto:${businessEmail}`} className="text-ink-muted hover:text-acid inline-flex items-center gap-1">
                    <Mail size={11} /> {businessEmail}
                  </a>
                </li>
              )}
              <li className="pt-2">
                <p className="text-eyebrow text-acid">{pickLocale(footer.newsletter_label, locale, 'Newsletter')}</p>
                <a
                  href={businessEmail ? `mailto:${businessEmail}?subject=Suscripción%20MODARIA` : '#contact'}
                  className="mt-2 inline-flex items-center gap-2 text-eyebrow text-ink hover:text-acid"
                  data-testid="footer-newsletter"
                >
                  {locale === 'es' ? 'Suscribirme' : 'Sign up'} <ArrowUpRight size={11} />
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom strip */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-3 pt-6 border-t border-line text-eyebrow text-ink-dim">
          <div>{pickLocale(site.footer_text, locale)}</div>
          <div className="flex items-center gap-4">
            <span>v1.0</span>
            <span>·</span>
            <span>{new Date().getFullYear()}</span>
            <span>·</span>
            <span className="text-acid">●</span>
            <span>{locale === 'es' ? 'Hecho en Madrid' : 'Made in Madrid'}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
